import { forwardRef, useState, useImperativeHandle, useCallback, type ReactNode } from 'react'
import {
  Tbody,
  Tr,
  Td,
  Checkbox,
  Badge,
  HStack,
  Image,
  Box,
  Menu,
  MenuButton,
  MenuList,
  IconButton,
} from '@chakra-ui/react'
import { MoreVertical, CheckCircle2 } from 'lucide-react'
import { Link as RouterLink } from 'react-router-dom'
import type { DataTableOptions, Permission } from './types'
import { getTableColumnCount, shouldShowActionsColumn } from './types'
import {
  buildRowActionItems,
  shouldRenderOtherActionsAsButtons,
  shouldRenderSingleOtherActionButton,
  renderRowActionMenuItems,
} from './rowActions'
import { isInteractiveContextMenuTarget, type RowContextMenuState } from './RowContextMenu'
import type { RowHoverHintState } from './RowActionHoverHint'
import { useThemeColors } from '@/hooks/useThemeColors'

interface TableBodyProps {
  options: DataTableOptions
  rows: any[]
  permissions?: Permission
  getRowPermissions?: (row: any) => Permission
  onAction?: (id: any, action: string) => void
  onCheckedAll?: (checked: boolean) => void
  customRenderers?: Record<string, (value: any, row?: any) => ReactNode>
  actionLoadingStates?: Record<string | number, boolean>
  customActionRenderer?: (row: any, isLoading?: boolean) => ReactNode
  onRowContextMenu?: (state: RowContextMenuState) => void
  onRowHoverHint?: (state: RowHoverHintState) => void
}

export interface TableBodyRef {
  checkedAll: (isCheckedAll: boolean) => void
  getSelectedData: () => number[]
}

export const TableBody = forwardRef<TableBodyRef, TableBodyProps>(
  (
    {
      options,
      rows,
      permissions,
      getRowPermissions,
      onAction,
      onCheckedAll,
      customRenderers,
      actionLoadingStates,
      customActionRenderer,
      onRowContextMenu,
      onRowHoverHint,
    },
    ref
  ) => {
    const { dataFields, primaryKey = 'id', otherActions, hideSelection } = options
    const hasPermission = permissions
    const useContextMenu = !!options.useRowContextMenu && !customActionRenderer
    const showActionsColumn = shouldShowActionsColumn(options, !!customActionRenderer)
    const columnCount = getTableColumnCount(options, !!customActionRenderer)

    const getEffectivePermission = (row: any): Permission => {
      if (!getRowPermissions || !row) return hasPermission ?? {}
      const rowPerm = getRowPermissions(row)
      return { ...hasPermission, ...rowPerm }
    }

    const [selectedData, setSelectedData] = useState<number[]>([])
    const [isCheckedAll, setIsCheckedAll] = useState(false)

    const handleCheckboxChange = (rowId: number, checked: boolean) => {
      const updatedSelectedData = checked ? [...selectedData, rowId] : selectedData.filter((id) => id !== rowId)
      setSelectedData(updatedSelectedData)

      if (updatedSelectedData.length === rows.length && rows.length > 0) {
        setIsCheckedAll(true)
        onCheckedAll?.(true)
      } else {
        setIsCheckedAll(false)
        onCheckedAll?.(false)
      }
    }

    const handleCheckAll = (checked: boolean) => {
      const allSelected = checked ? rows.map((row) => row[primaryKey]) : []
      setSelectedData(allSelected)
      setIsCheckedAll(checked)
    }

    useImperativeHandle(ref, () => ({
      checkedAll: handleCheckAll,
      getSelectedData: () => selectedData,
    }))

    const getRowActionItems = useCallback(
      (row: any, rowId: any, delDate: any) => {
        return buildRowActionItems({
          row,
          rowId,
          delDate,
          options,
          effectivePerm: getEffectivePermission(row),
          onAction,
          forceMenuItems: useContextMenu,
        })
      },
      [options, onAction, useContextMenu, getRowPermissions, hasPermission]
    )

    const handleRowContextMenu = useCallback(
      (e: React.MouseEvent, row: any, rowId: any, delDate: any) => {
        if (!useContextMenu || !onRowContextMenu) return
        if (isInteractiveContextMenuTarget(e.target)) return

        const items = getRowActionItems(row, rowId, delDate)
        if (items.length === 0) return

        e.preventDefault()
        onRowContextMenu({
          isOpen: true,
          x: e.clientX,
          y: e.clientY,
          items,
        })
      },
      [useContextMenu, getRowActionItems, onRowContextMenu]
    )

    const renderActions = (id: any, row: any, delDate: any) => {
      if (customActionRenderer) {
        const isLoading = actionLoadingStates?.[id] || false
        return <Box onClick={(e) => e.stopPropagation()}>{customActionRenderer(row, isLoading)}</Box>
      }

      const items = getRowActionItems(row, id, delDate)

      if (
        shouldRenderOtherActionsAsButtons(row, delDate, options, items.length, useContextMenu)
      ) {
        const visibleOtherActions = Array.isArray(otherActions)
          ? otherActions.filter((action) => !action.show || action.show(row))
          : []

        return (
          <HStack spacing={2} onClick={(e) => e.stopPropagation()}>
            {visibleOtherActions.map((action, index) => {
              const actionLoading = actionLoadingStates?.[id] || false
              return (
                <Box key={`action-${index}`}>
                  {action.render?.(row, actionLoading, () => action.onClick?.(row))}
                </Box>
              )
            })}
          </HStack>
        )
      }

      if (shouldRenderSingleOtherActionButton(row, delDate, options, items.length, useContextMenu)) {
        const action = otherActions![0]
        return (
          <IconButton
            aria-label={action.name}
            icon={<CheckCircle2 size={18} />}
            variant="ghost"
            size="sm"
            colorScheme="blue"
            onClick={(e) => {
              e.stopPropagation()
              action.onClick?.(row)
            }}
          />
        )
      }

      if (items.length === 0) return <Box width="100%" />

      return (
        <Menu placement="bottom-end" strategy="fixed">
          <MenuButton
            as={IconButton}
            aria-label="Actions"
            icon={<MoreVertical size={18} />}
            variant="ghost"
            size="sm"
            onClick={(e) => e.stopPropagation()}
          />
          <MenuList minW="180px" zIndex={2000} onClick={(e) => e.stopPropagation()}>
            {renderRowActionMenuItems(items, RouterLink)}
          </MenuList>
        </Menu>
      )
    }

    const colors = useThemeColors()
    const rowHoverBg = colors.sectionBg
    const rowStripeBg = 'rgba(0, 0, 0, 0.05)'
    const emptyTextColor = colors.textSecondary

    if (rows.length === 0) {
      return (
        <Tbody>
          <Tr>
            <Td colSpan={columnCount} textAlign="center" py={8}>
              <Box color={emptyTextColor}>{options.noRecordText || 'No Record Found'}</Box>
            </Td>
          </Tr>
        </Tbody>
      )
    }

    return (
      <Tbody>
          {rows.map((row, rowIndex) => {
            const rowId = row[primaryKey]
            const delDate = row.deleted_at
            const rowActionItems = useContextMenu ? getRowActionItems(row, rowId, delDate) : []
            const hasActions = rowActionItems.length > 0

            const showRowActionHint = useContextMenu && hasActions

            return (
              <Tr
                key={rowIndex}
                bg={rowIndex % 2 === 1 ? rowStripeBg : 'transparent'}
                _hover={
                  showRowActionHint
                    ? { bg: rowHoverBg, boxShadow: `inset 3px 0 0 ${colors.brandBlue}` }
                    : { bg: rowHoverBg }
                }
                cursor={showRowActionHint ? 'context-menu' : undefined}
                onContextMenu={(e) => handleRowContextMenu(e, row, rowId, delDate)}
                onMouseEnter={(e) => {
                  if (!showRowActionHint || !onRowHoverHint) return
                  const rect = e.currentTarget.getBoundingClientRect()
                  onRowHoverHint({
                    visible: true,
                    top: rect.top,
                    left: rect.left + 16,
                  })
                }}
                onMouseLeave={() => onRowHoverHint?.({ visible: false, top: 0, left: 0 })}
              >
                {!hideSelection && (
                  <Td py={2} px={4}>
                    <Checkbox
                      isChecked={selectedData.includes(rowId)}
                      onChange={(e) => {
                        e.stopPropagation()
                        handleCheckboxChange(rowId, e.target.checked)
                      }}
                      onClick={(e) => e.stopPropagation()}
                      colorScheme="blue"
                    />
                  </Td>
                )}
                {Object.keys(dataFields).map((field, index) => {
                  const isFullWidth = dataFields[field].fullWidth === true
                  const noTruncate = dataFields[field].noTruncate === true
                  const columnMaxWidth = isFullWidth || noTruncate ? undefined : (dataFields[field].maxWidth || '350px')
                  const isFirstColumn = index === 0
                  const isDateField =
                    /(_at|date|time)$/i.test(field) || /\b(date|time)\b/i.test(dataFields[field].name || '')

                  return (
                    <Td
                      key={field}
                      {...(columnMaxWidth != null && { maxW: columnMaxWidth })}
                      minW={isFullWidth ? 'min-content' : noTruncate ? '12rem' : undefined}
                      overflow={isFullWidth || noTruncate ? 'visible' : 'hidden'}
                      textOverflow={isFullWidth || noTruncate ? 'clip' : 'ellipsis'}
                      className={dataFields[field].className}
                      whiteSpace={isFirstColumn || isDateField ? 'nowrap' : 'normal'}
                      py={2}
                      px={4}
                    >
                      {dataFields[field].attachment &&
                        (() => {
                          const attachmentField =
                            typeof dataFields[field].attachment === 'string' ? dataFields[field].attachment : field
                          const imageUrl = row[attachmentField]
                          return imageUrl ? (
                            <Image
                              src={imageUrl}
                              alt={imageUrl || 'Image'}
                              width="60px"
                              height="60px"
                              objectFit="cover"
                              borderRadius="md"
                              fallbackSrc="/assets/img/no-image.svg"
                              mr={2}
                              display="inline-block"
                              verticalAlign="middle"
                            />
                          ) : null
                        })()}
                      {customRenderers && customRenderers[field] ? (
                        customRenderers[field](row[field], row)
                      ) : dataFields[field].badge ? (
                        <Badge colorScheme={dataFields[field].badge[row[field]] || 'gray'} variant="solid">
                          {dataFields[field].badgeLabels
                            ? dataFields[field].badgeLabels[row[field]] || row[field]
                            : row[field]}
                        </Badge>
                      ) : (
                        <span>
                          {field === 'file_size' && row[field]
                            ? (() => {
                                const bytes = typeof row[field] === 'number' ? row[field] : parseInt(row[field])
                                if (isNaN(bytes)) return row[field]
                                const units = ['B', 'KB', 'MB', 'GB', 'TB']
                                let size = bytes
                                let unit = 0
                                while (size >= 1024 && unit < units.length - 1) {
                                  size /= 1024
                                  unit++
                                }
                                return `${size.toFixed(2)} ${units[unit]}`
                              })()
                            : row[field]}
                        </span>
                      )}
                    </Td>
                  )
                })}
                {showActionsColumn && (
                  <Td width="80px" textAlign="right" py={2} px={4}>
                    {renderActions(rowId, row, delDate)}
                  </Td>
                )}
              </Tr>
            )
          })}
      </Tbody>
    )
  }
)

TableBody.displayName = 'TableBody'
